// 📄 catalogoInterfacesService.ts
// Catálogo global de Interfaces/Canales de integración (SAP Ariba ↔ ERP ↔
// S/4HANA ↔ BTP): documenta por dónde entra/sale cada flujo y en qué
// transacción/herramienta se monitorea. Mismo criterio que
// catalogoTransaccionesService.ts / catalogoBadisService.ts: CRUD completo
// solo desde el panel Admin, lectura abierta a cualquier consultor
// autenticado (ver firestore.rules, colección `catalogoInterfaces`).
import {
  doc,
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';
import {
  SolucionTransaccion, SOLUCIONES_TRANSACCION, solucionTransaccionMeta,
  ObjetoNegocio, OBJETOS_NEGOCIO, objetoNegocioMeta
} from './catalogoTransaccionesService.ts';

// Reutiliza el catálogo de Solución (y de Objeto de Negocio) de
// Transacciones (mismo dominio de negocio) en vez de duplicarlo — ver
// [[catalogoTransaccionesService.ts]].
export { SOLUCIONES_TRANSACCION as SOLUCIONES_INTERFAZ, solucionTransaccionMeta as solucionInterfazMeta };
export { OBJETOS_NEGOCIO, objetoNegocioMeta };
export type SolucionInterfaz = SolucionTransaccion;
export type { ObjetoNegocio };

export type PlataformaInterfaz = 'erp' | 's4hana' | 'btp' | 'cig' | 'ariba' | 'docusign' | 'otro';

export interface PlataformaInterfazMeta {
  id: PlataformaInterfaz;
  label: string;
}

export const PLATAFORMAS_INTERFAZ: PlataformaInterfazMeta[] = [
  { id: 'erp', label: 'ERP' },
  { id: 's4hana', label: 'S/4HANA Cloud' },
  { id: 'btp', label: 'SAP BTP' },
  { id: 'cig', label: 'CIG' },
  { id: 'ariba', label: 'SAP Ariba' },
  { id: 'docusign', label: 'DocuSign' },
  { id: 'otro', label: 'Otro' }
];

export const plataformaInterfazMeta = (plataforma: string): PlataformaInterfazMeta =>
  PLATAFORMAS_INTERFAZ.find((p) => p.id === plataforma) || PLATAFORMAS_INTERFAZ[PLATAFORMAS_INTERFAZ.length - 1];

export type DireccionInterfaz = 'ingreso' | 'salida' | 'bidireccional';

export interface DireccionInterfazMeta {
  id: DireccionInterfaz;
  label: string;
  // Texto de la opción del select, parametrizado por el nombre de la
  // plataforma elegida (ver "Decisión de diseño" — dirección es RELATIVA
  // a `plataforma`, ambigua sin esa referencia). `labelCorta` es lo que se
  // muestra en el badge de la card, donde no hay espacio para la frase
  // completa.
  labelCorta: string;
  opcion: (nombrePlataforma: string) => string;
}

export const DIRECCIONES_INTERFAZ: DireccionInterfazMeta[] = [
  {
    id: 'ingreso',
    label: 'Ingreso',
    labelCorta: 'Ingreso',
    opcion: (plataforma) => `Ingreso (entrante hacia ${plataforma})`
  },
  {
    id: 'salida',
    label: 'Salida',
    labelCorta: 'Salida',
    opcion: (plataforma) => `Salida (saliente desde ${plataforma})`
  },
  {
    id: 'bidireccional',
    label: 'Bidireccional',
    labelCorta: 'Bidireccional',
    opcion: () => 'Bidireccional'
  }
];

export const direccionInterfazMeta = (direccion: string): DireccionInterfazMeta =>
  DIRECCIONES_INTERFAZ.find((d) => d.id === direccion) || DIRECCIONES_INTERFAZ[0];

export const TECNOLOGIAS_INTERFAZ = ['cXML', 'IDoc', 'SOAP', 'OData', 'REST', 'CSV', 'Proxy ABAP', 'Otro'];
export const MIDDLEWARES_INTERFAZ = ['CIG', 'Integration Suite / CPI', 'PI-PO', 'Directo', 'N/A'];

export interface InterfaceChannelData {
  // --- Núcleo (requeridos) ---
  // Caso de uso real: el usuario sabe QUÉ documento le llegó mal ("un
  // contrato", "una factura"), no en qué transacción buscarlo — objetoNegocio
  // es el punto de entrada, nombreTecnico el valor que termina pegando en
  // esa transacción (ver InterfaceFormModal.tsx / InterfaceCatalogGrid.tsx).
  // '' solo aparece en docs creados antes de este campo (ver getInterfaces)
  // — el form siempre exige un valor real antes de guardar.
  objetoNegocio: ObjetoNegocio | '';
  solucion: SolucionInterfaz;
  plataforma: PlataformaInterfaz;
  transaccion: string;
  direccion: DireccionInterfaz;
  textoBreve: string;
  // Nombre del servicio/iFlow/canal — según la tecnología es un Interface
  // Name, un Basic Type, un iFlow, etc. Es el "campo estrella": lo que el
  // usuario copia y pega en `campoBusqueda` dentro de `transaccion`.
  nombreTecnico: string;

  // --- Opcionales ---
  // Dónde pegar `nombreTecnico` dentro de `transaccion` — ej. "Receiver
  // Information > Interface Name" (SRT_MONI), "Basic Type" (WE02).
  campoBusqueda: string;
  // Sinónimos para que la búsqueda de texto encuentre esta interfaz aunque
  // el usuario no escriba el nombre técnico ni el código de transacción.
  palabrasClave: string[];
  // FK al doc de `catalogoTransacciones` cuando `transaccion` coincide
  // exactamente con un código ya catalogado (ver InterfaceFormModal.tsx) —
  // habilita el link "ver en Transacciones" en la card. Si no hay
  // coincidencia, `transaccion` sigue siendo texto libre y esto queda null.
  transaccionRefId: string | null;
  descripcion: string;
  tecnologia: string;
  middleware: string;
  activo: boolean;
}

export interface InterfaceChannel extends InterfaceChannelData {
  id: string;
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
}

const CATALOGO_COLLECTION = "catalogoInterfaces";

export const getInterfaces = async (): Promise<InterfaceChannel[]> => {
  const snap = await getDocs(collection(db, CATALOGO_COLLECTION));
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      // objetoNegocio/campoBusqueda/palabrasClave son campos nuevos — un
      // doc creado antes de esta iteración no los tiene. Se normalizan acá
      // ('' / [] ) para que el resto de la app (filtros, cards) nunca vea
      // undefined; la UI muestra "Sin clasificar" cuando objetoNegocio
      // queda vacío (ver objetoNegocioMeta, que devuelve null en ese caso).
      objetoNegocio: (data.objetoNegocio as ObjetoNegocio) || '',
      solucion: (data.solucion as SolucionInterfaz) || 'general',
      plataforma: (data.plataforma as PlataformaInterfaz) || 'erp',
      transaccion: data.transaccion || '',
      direccion: (data.direccion as DireccionInterfaz) || 'ingreso',
      textoBreve: data.textoBreve || '',
      nombreTecnico: data.nombreTecnico || '',
      campoBusqueda: data.campoBusqueda || '',
      palabrasClave: Array.isArray(data.palabrasClave) ? data.palabrasClave : [],
      transaccionRefId: data.transaccionRefId || null,
      descripcion: data.descripcion || '',
      tecnologia: data.tecnologia || '',
      middleware: data.middleware || '',
      activo: data.activo !== false,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      createdBy: data.createdBy || ''
    };
  });
};

/**
 * Crea (interfaceId = null) o actualiza (interfaceId = id existente) un
 * registro. Igual que catalogoTransaccionesService.ts, el id lo genera el
 * llamador cuando es nuevo (ver generarId en InterfaceFormModal.tsx).
 */
export const saveInterface = async (
  interfaceId: string,
  data: InterfaceChannelData,
  esNuevo: boolean,
  createdBy: string
): Promise<void> => {
  if (!data.objetoNegocio) throw new Error("El objeto de negocio es obligatorio.");
  if (!data.solucion) throw new Error("La solución es obligatoria.");
  if (!data.plataforma) throw new Error("La plataforma es obligatoria.");
  if (!data.transaccion.trim()) throw new Error("La transacción es obligatoria.");
  if (!data.direccion) throw new Error("La dirección es obligatoria.");
  if (!data.textoBreve.trim()) throw new Error("El texto breve es obligatorio.");
  if (!data.nombreTecnico.trim()) throw new Error("El nombre técnico / valor a buscar es obligatorio.");

  const payload: Record<string, any> = {
    objetoNegocio: data.objetoNegocio,
    solucion: data.solucion,
    plataforma: data.plataforma,
    transaccion: data.transaccion.trim(),
    direccion: data.direccion,
    textoBreve: data.textoBreve.trim(),
    nombreTecnico: data.nombreTecnico.trim(),
    campoBusqueda: data.campoBusqueda.trim(),
    palabrasClave: data.palabrasClave.map((p) => p.trim()).filter(Boolean),
    transaccionRefId: data.transaccionRefId || null,
    descripcion: data.descripcion.trim(),
    tecnologia: data.tecnologia,
    middleware: data.middleware,
    activo: data.activo,
    updatedAt: serverTimestamp()
  };
  if (esNuevo) {
    payload.createdAt = serverTimestamp();
    payload.createdBy = createdBy;
  }

  await setDoc(doc(db, CATALOGO_COLLECTION, interfaceId), payload, { merge: true });
};

export const deleteInterface = async (interfaceId: string): Promise<void> => {
  await deleteDoc(doc(db, CATALOGO_COLLECTION, interfaceId));
};

// ── Sembrado inicial ──────────────────────────────────────────────────
// Ids determinísticos (a partir de solución+transacción) para que sembrar
// sea idempotente — mismo criterio que seedCatalogoTransacciones.
const slugificarId = (solucion: string, transaccion: string) =>
  `${solucion}-${transaccion}`.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// Nota: el seed original de "Contratos" (SRT_MONI) se quitó a propósito —
// ya existe un registro creado a mano en producción/QAS con esos datos
// reales, bajo un id distinto al determinístico de este slug. Sembrarlo de
// nuevo acá crearía un duplicado en vez de actualizar ese registro. Si
// alguna vez se borra el registro manual, se puede volver a agregar acá.
const SEED_INTERFACES: InterfaceChannelData[] = [
  {
    objetoNegocio: 'proveedores',
    solucion: 'slp',
    plataforma: 'erp',
    transaccion: 'WE02',
    campoBusqueda: 'Basic Type',
    direccion: 'salida',
    textoBreve: 'Replicación de proveedores desde ERP hacia Ariba SLP',
    transaccionRefId: null,
    descripcion: 'Replicación de proveedores desde ERP hacia Ariba SLP.',
    tecnologia: 'IDoc',
    middleware: 'CIG',
    nombreTecnico: 'CREMAS',
    palabrasClave: ['proveedor', 'vendor', 'supplier', 'SLP', 'CREMAS'],
    activo: true
  },
  {
    objetoNegocio: 'ordenesCompra',
    solucion: 'buying',
    plataforma: 'erp',
    transaccion: 'WE02',
    campoBusqueda: 'Basic Type',
    direccion: 'salida',
    textoBreve: 'Órdenes de compra enviadas desde Ariba Buying al ERP',
    transaccionRefId: null,
    // ORDERS05 es el Basic Type estándar SAP para órdenes de compra — a
    // verificar en WE02 contra el sistema real, podría estar customizado
    // como ARBCIG_ORDERS (ver ARBCIG_INVOIC del seed de Facturas).
    descripcion: 'Órdenes de compra enviadas desde Ariba Buying al ERP. Verificar en WE02 si el Basic Type real es ORDERS05 o una variante ARBCIG_*.',
    tecnologia: 'cXML',
    middleware: 'CIG',
    nombreTecnico: 'ORDERS05',
    palabrasClave: ['orden de compra', 'purchase order', 'PO'],
    activo: true
  },
  {
    objetoNegocio: 'facturas',
    solucion: 'commerce',
    plataforma: 'erp',
    transaccion: 'WE02',
    campoBusqueda: 'Basic Type',
    direccion: 'ingreso',
    textoBreve: 'Interfaz de ingreso de facturas recibidas desde Ariba al ERP',
    transaccionRefId: null,
    descripcion: 'Interfaz de ingreso de facturas recibidas desde Ariba al ERP.',
    tecnologia: 'IDoc',
    middleware: 'CIG',
    nombreTecnico: 'ARBCIG_INVOIC',
    palabrasClave: ['factura', 'invoice', 'INVOIC', 'FV'],
    activo: true
  }
];

/**
 * Carga los registros de ejemplo (ver SEED_INTERFACES). Idempotente: usa
 * un id determinístico por solución+transacción, así correrlo de nuevo
 * actualiza los mismos registros en vez de duplicarlos.
 */
export const seedCatalogoInterfaces = async (createdBy: string): Promise<void> => {
  await Promise.all(
    SEED_INTERFACES.map((data) => saveInterface(slugificarId(data.solucion, data.transaccion), data, true, createdBy))
  );
};
